import { Box, Card, Grid, Typography } from '@mui/material'
import { Table } from 'antd'
import React from 'react'

import { numberFormat } from '../../../../../utils'

export interface IReturnReportSummary {
  orders: number
  total: number
  codTotal: number
  bankTotal: number
}

export interface IReturnReportProduct {
  product: string
  productCode: string
  productName: string
  productImageUrl?: string
  category: string
  categoryName: string
  meta?: Record<string, any>
  orders: number
  quantity: number
  total: number
  cost: number
  codTotal: number
  bankTotal: number
}

export interface IReturnReportCategory {
  category: string
  categoryName: string
  orders: number
  quantity: number
  total: number
}

export interface IReturnReport {
  summary?: IReturnReportSummary
  products?: IReturnReportProduct[]
  categories?: IReturnReportCategory[]
}

export interface IDailyReturnReportTableProps {
  data?: IReturnReport
  isLoading: boolean
}

const SummaryCard = (props: { label: string; value: string }) => (
  <Card sx={{ padding: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {props.label}
    </Typography>
    <Typography variant="h6">{props.value}</Typography>
  </Card>
)

const DailyReturnReportTable = React.forwardRef(
  (props: IDailyReturnReportTableProps, ref: any) => {
    const { data, isLoading } = props
    const summary = data?.summary
    const products = data?.products ?? []
    const categories = data?.categories ?? []

    const quantityTotal = products.reduce(
      (acc, item) => acc + (item.quantity ?? 0),
      0,
    )

    const productColumns = [
      {
        title: 'ชื่อสินค้า',
        dataIndex: 'productName',
        key: 'productName',
        render: (value: string, record: IReturnReportProduct) => (
          <Box>
            <Typography variant="body2">{value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {record.productCode}
              {record.meta?.size ? ` · ${record.meta.size}` : ''}
              {record.meta?.color ? ` · ${record.meta.color}` : ''}
            </Typography>
          </Box>
        ),
      },
      {
        title: 'หมวดหมู่',
        dataIndex: 'categoryName',
        key: 'categoryName',
      },
      {
        title: 'ออเดอร์',
        dataIndex: 'orders',
        key: 'orders',
        align: 'right' as const,
        render: (value: number) => numberFormat(value, 0),
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right' as const,
        render: (value: number) => numberFormat(value, 0),
      },
      {
        title: 'มูลค่า',
        dataIndex: 'total',
        key: 'total',
        align: 'right' as const,
        render: (value: number) => numberFormat(value),
      },
      {
        title: 'ต้นทุน',
        dataIndex: 'cost',
        key: 'cost',
        align: 'right' as const,
        render: (value: number) => numberFormat(value),
      },
    ]

    const categoryColumns = [
      {
        title: 'หมวดหมู่',
        dataIndex: 'categoryName',
        key: 'categoryName',
      },
      {
        title: 'ออเดอร์',
        dataIndex: 'orders',
        key: 'orders',
        align: 'right' as const,
        render: (value: number) => numberFormat(value, 0),
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right' as const,
        render: (value: number) => numberFormat(value, 0),
      },
      {
        title: 'มูลค่า',
        dataIndex: 'total',
        key: 'total',
        align: 'right' as const,
        render: (value: number) => numberFormat(value),
      },
    ]

    return (
      <Box ref={ref}>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} lg={3}>
            <SummaryCard
              label="ออเดอร์ตีกลับ"
              value={numberFormat(summary?.orders ?? 0, 0)}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <SummaryCard
              label="จำนวนสินค้า (ชิ้น)"
              value={numberFormat(quantityTotal, 0)}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <SummaryCard
              label="มูลค่ารวม"
              value={numberFormat(summary?.total ?? 0)}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <SummaryCard
              label="COD / โอน"
              value={`${numberFormat(summary?.codTotal ?? 0)} / ${numberFormat(
                summary?.bankTotal ?? 0,
              )}`}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" mb={1}>
          แยกตามสินค้า
        </Typography>
        <Table
          rowKey={(record: IReturnReportProduct) =>
            `${record.product}-${record.meta?.size ?? ''}-${
              record.meta?.color ?? ''
            }`
          }
          loading={isLoading}
          columns={productColumns}
          dataSource={products}
          pagination={false}
          scroll={{ x: true }}
          size="small"
        />

        <Typography variant="subtitle1" mt={3} mb={1}>
          แยกตามหมวดหมู่
        </Typography>
        <Table
          rowKey="category"
          loading={isLoading}
          columns={categoryColumns}
          dataSource={categories}
          pagination={false}
          scroll={{ x: true }}
          size="small"
        />
      </Box>
    )
  },
)

export default DailyReturnReportTable
